import { Option } from '@joor/design-system'
import { isEmpty } from 'lodash'

import { ShippingAddress, ShippingInfo } from '__generated__/atlas-types'

import { RetailerLocation } from '../../EditAddressesModal/hooks/useAddressModalData'
import { BillingAddressFormValues } from '../BillingAddressForm/billingAddressForm.types'
import {
  GetAddressOptionsInputType,
  OptionsRecord,
  cleanupLocationId,
  extractCompanyFromLabel,
  formatBrandAddressOptions,
  formatOrderAddressOption,
  formatPrimaryLocationOption,
  formatRetailerLocationOptions,
  getAddressOptionsForBrands,
  getAddressOptionsForLiteRetailers,
  getCountryOption,
} from '../forms.utils'
import {
  FieldIds,
  ShippingAddressFormValues,
} from './shippingAddressForm.types'
import { OrderBillingShippingData } from 'features/Orders/Review/components/Overview/components/AddressesWidget/addressesWidget.queries'
import { isOrderAddressIncluded } from 'features/Orders/Review/components/Overview/components/AddressesWidget/addressesWidget.utils'

type FormatterToFormValuesType = {
  selected: Option | null
  countryOption: Option | undefined
  trackingNumber?: string | null
  shippingMethodOption?: Option | null
  doorOption?: Option | null | string
  email?: string | null
}

export type InitialShippingFormValuesInput = {
  orderShipping: OrderBillingShippingData['shipping'] | undefined
  countryOptions: Option[]
  shippingAddressOptions: Option[]
}

export const getInitialShippingFormValues = ({
  orderShipping,
  countryOptions,
  shippingAddressOptions,
}: InitialShippingFormValuesInput): ShippingAddressFormValues => {
  const foundSelected = shippingAddressOptions.find(
    (address) =>
      extractCompanyFromLabel(address.label) === orderShipping?.address.company,
  )

  return formatOrderShippingToFormValues({
    selected: foundSelected?.label
      ? {
          ...foundSelected,
          label: extractCompanyFromLabel(foundSelected.label),
        }
      : null,
    orderShipping,
    countryOption: getCountryOption(
      orderShipping?.address.country,
      countryOptions,
    ),
  })
}

export const formatShippingAddressToFormValues = ({
  selected,
  shippingAddress,
  countryOption,
  trackingNumber,
  shippingMethodOption,
  doorOption,
}: FormatterToFormValuesType & {
  shippingAddress: ShippingAddress | undefined
}): ShippingAddressFormValues => {
  const { email, address, code, phoneNumber } = shippingAddress || {}
  return {
    [FieldIds.AddressOption]: selected,
    [FieldIds.CompanyName]: address?.companyName || '',
    [FieldIds.RecipientName]: address?.name || '',
    [FieldIds.Email]: email || '',
    [FieldIds.Address1]: address?.line1 || '',
    [FieldIds.Address2]: address?.line2 || '',
    [FieldIds.City]: address?.city || '',
    [FieldIds.Territory]: address?.state || '',
    [FieldIds.Country]: countryOption || null,
    [FieldIds.PostalCode]: address?.zip || '',
    [FieldIds.ShippingCode]: code || '',
    [FieldIds.Phone]: phoneNumber || '',
    [FieldIds.TrackingNumber]: trackingNumber || '',
    [FieldIds.ShippingMethod]: shippingMethodOption || null,
    [FieldIds.Door]: doorOption || null,
  }
}

/**
 * Only used when "isSameAddressForBillingAndShipping" checkbox is checked.
 */
export const formatBillingFormValuesToShippingFormValues = (
  billingFormValues: BillingAddressFormValues,
): ShippingAddressFormValues => ({
  [FieldIds.AddressOption]: billingFormValues?.addressOption
    ? { label: billingFormValues?.addressOption?.label, value: '' }
    : null,
  [FieldIds.CompanyName]: billingFormValues?.companyName ?? '',
  [FieldIds.RecipientName]: billingFormValues?.recipientName ?? '',
  [FieldIds.Address1]: billingFormValues?.address1 ?? '',
  [FieldIds.Address2]: billingFormValues?.address2 ?? '',
  [FieldIds.City]: billingFormValues?.city ?? '',
  [FieldIds.Territory]: billingFormValues?.territory ?? '',
  [FieldIds.Country]: billingFormValues?.country ?? null,
  [FieldIds.PostalCode]: billingFormValues?.postalCode ?? '',
  [FieldIds.Phone]: billingFormValues?.phone ?? '',
})

export const formatOrderShippingToFormValues = ({
  selected,
  orderShipping,
  countryOption,
  trackingNumber,
  shippingMethodOption,
  doorOption,
}: FormatterToFormValuesType & {
  orderShipping: OrderBillingShippingData['shipping'] | undefined
}): ShippingAddressFormValues => ({
  [FieldIds.AddressOption]: selected,
  [FieldIds.CompanyName]: orderShipping?.address?.company ?? '',
  [FieldIds.RecipientName]: orderShipping?.address?.name ?? '',
  [FieldIds.Email]: orderShipping?.address?.email ?? '',
  [FieldIds.Address1]: orderShipping?.address?.firstAddress ?? '',
  [FieldIds.Address2]: orderShipping?.address?.secondAddress ?? '',
  [FieldIds.City]: orderShipping?.address?.city ?? '',
  [FieldIds.Territory]: orderShipping?.address?.state ?? '',
  [FieldIds.Country]: countryOption ?? null,
  [FieldIds.PostalCode]: orderShipping?.address?.zip ?? '',
  [FieldIds.ShippingCode]:
    orderShipping?.address?.sourceShippingAddress?.code ?? '',
  [FieldIds.Phone]: orderShipping?.address?.phone ?? '',
  [FieldIds.TrackingNumber]: trackingNumber ?? '',
  [FieldIds.ShippingMethod]: shippingMethodOption ?? null,
  [FieldIds.Door]: doorOption ?? null,
})

export const formatRetailerLocationToFormValues = ({
  selected,
  retailerLocation,
  countryOption,
  trackingNumber,
  shippingMethodOption,
  doorOption,
  email,
}: FormatterToFormValuesType & {
  retailerLocation: RetailerLocation | undefined
}): ShippingAddressFormValues => ({
  [FieldIds.AddressOption]: selected,
  [FieldIds.CompanyName]: retailerLocation?.name ?? '',
  // we don't have a recipient name for the primary location yet
  // even we send it empty, the response back has the source address name
  [FieldIds.RecipientName]: '',
  [FieldIds.Email]: email ?? '',
  [FieldIds.Address1]: retailerLocation?.address1 ?? '',
  [FieldIds.Address2]: retailerLocation?.address2 ?? '',
  [FieldIds.City]: retailerLocation?.city ?? '',
  [FieldIds.Territory]: retailerLocation?.state ?? '',
  [FieldIds.Country]: countryOption ?? null,
  [FieldIds.PostalCode]: retailerLocation?.zip ?? '',
  [FieldIds.ShippingCode]: '',
  [FieldIds.Phone]: retailerLocation?.phone ?? '',
  [FieldIds.TrackingNumber]: trackingNumber ?? '',
  [FieldIds.ShippingMethod]: shippingMethodOption ?? null,
  [FieldIds.Door]: doorOption ?? null,
})

export const getShippingAddressOptions = ({
  shippingAddresses,
  primaryLocation,
  shipping,
  retailerLocations,
  isAccountTypeBrand,
}: {
  shippingAddresses: ShippingAddress[]
  primaryLocation?: RetailerLocation
  retailerLocations?: RetailerLocation[]
  shipping?: OrderBillingShippingData['shipping']
  isAccountTypeBrand?: boolean
}): OptionsRecord => {
  const brandAddresses = shippingAddresses.map(({ address }) => address)
  const orderAddress = shipping?.address

  const addressOptions: GetAddressOptionsInputType = {
    orderAddressOption: formatOrderAddressOption(orderAddress),
    primaryLocationOption: formatPrimaryLocationOption(primaryLocation),
    retailerLocationsOptions: formatRetailerLocationOptions(
      retailerLocations ?? [],
    ),
    brandAddressOptions: formatBrandAddressOptions(shippingAddresses),
    shouldAddOrderAddress:
      !isEmpty(orderAddress) &&
      !isOrderAddressIncluded(brandAddresses, orderAddress),
  }

  return isAccountTypeBrand
    ? getAddressOptionsForBrands(addressOptions)
    : getAddressOptionsForLiteRetailers(addressOptions)
}

export const formatShippingFormValuesToShippingInfo = (
  formValues: ShippingAddressFormValues,
): ShippingInfo => ({
  shippingAddressId: cleanupLocationId(formValues[FieldIds.AddressOption]),
  address: {
    // this value is required to have value by the mutation, but it might come as null from the BE query
    // that's why we send an empty string in that case
    companyName: formValues[FieldIds.CompanyName] ?? '',
    recipientName: formValues[FieldIds.RecipientName],
    city: formValues[FieldIds.City],
    country: formValues[FieldIds.Country]?.label,
    line1: formValues[FieldIds.Address1],
    line2: formValues[FieldIds.Address2],
    state: formValues[FieldIds.Territory],
    zip: formValues[FieldIds.PostalCode],
  },
  email: formValues[FieldIds.Email],
  phoneNumber: formValues[FieldIds.Phone],
  trackingNumber: formValues[FieldIds.TrackingNumber],
  shippingMethodId: formValues[FieldIds.ShippingMethod]?.value,
})
